import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  CheckSquare, 
  MoreVertical, 
  Trash2, 
  ArrowRight,
  Shield,
  Users,
  FileText,
  Target,
  Plane,
  Repeat,
  Plus,
  Calendar,
  Clock
} from "lucide-react";

const priorityConfig = {
  Today: { label: "Today", className: "badge-today border" },
  ThisWeek: { label: "This Week", className: "badge-thisweek border" },
  ParkingLot: { label: "Parking Lot", className: "badge-parking border" },
};

const categoryIcons = {
  Safety: Shield,
  "Patient Safety": Shield,
  Staff: Users,
  "Staff & Culture": Users,
  Admin: FileText,
  "Admin/Regulatory": FileText,
  Strategic: Target,
};

const categoryColors = {
  Safety: "text-red-400",
  "Patient Safety": "text-red-400",
  Staff: "text-blue-400",
  "Staff & Culture": "text-blue-400",
  Admin: "text-amber-400",
  "Admin/Regulatory": "text-amber-400",
  Strategic: "text-emerald-400",
};

const recurrenceConfig = {
  weekly: { label: "Weekly", color: "bg-blue-500/10 border-blue-500/30 text-blue-400" },
  biweekly: { label: "Bi-weekly", color: "bg-cyan-500/10 border-cyan-500/30 text-cyan-400" },
  monthly: { label: "Monthly", color: "bg-violet-500/10 border-violet-500/30 text-violet-400" },
  quarterly: { label: "Quarterly", color: "bg-amber-500/10 border-amber-500/30 text-amber-400" },
  yearly: { label: "Yearly", color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" },
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTask, setNewTask] = useState({
    description: "",
    category: "Admin",
    priority: "Today",
    recurrence: null,
    due_date: ""
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(response.data || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const toggleComplete = async (task) => {
    try {
      const response = await axios.patch(`${API}/tasks/${task.task_id}`, {
        completed: !task.completed
      }, { withCredentials: true });
      
      // For recurring tasks, show a different message
      if (task.recurrence && !task.completed) {
        const nextDue = response.data.due_date;
        toast.success("Task completed!", { 
          description: `Next occurrence: ${nextDue ? new Date(nextDue).toLocaleDateString() : 'soon'}` 
        });
      } else if (!task.completed) {
        toast.success("Task completed!", { description: "Great work!" });
      }
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const createTask = async () => {
    if (!newTask.description.trim()) {
      toast.error("Please enter a task description");
      return;
    }

    try {
      await axios.post(`${API}/tasks`, {
        description: newTask.description,
        category: newTask.category,
        priority: newTask.priority,
        recurrence: newTask.recurrence || null,
        due_date: newTask.due_date || null
      }, { withCredentials: true });

      toast.success(newTask.recurrence 
        ? `Recurring task created (${recurrenceConfig[newTask.recurrence].label})!`
        : "Task created!"
      );
      
      setShowCreateDialog(false);
      setNewTask({
        description: "",
        category: "Admin",
        priority: "Today",
        recurrence: null,
        due_date: ""
      });
      fetchTasks();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const updatePriority = async (task, newPriority) => {
    try {
      await axios.patch(`${API}/tasks/${task.task_id}`, {
        priority: newPriority
      }, { withCredentials: true });
      toast.success(`Moved to ${priorityConfig[newPriority].label}`);
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Hide completed tasks by default — toggle "Show completed" surfaces them again.
  // Completed-count is kept around so the toggle can show "(X done)".
  const completedCount = tasks.filter(t => t.completed).length;
  const visibleTasks = showCompleted ? tasks : tasks.filter(t => !t.completed);

  const groupedTasks = {
    Today: visibleTasks.filter(t => t.priority === "Today"),
    ThisWeek: visibleTasks.filter(t => t.priority === "ThisWeek"),
    ParkingLot: visibleTasks.filter(t => t.priority === "ParkingLot"),
  };

  const filteredGroups = filter === "all"
    ? groupedTasks
    : { [filter]: groupedTasks[filter] };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="tasks-page" className="space-y-6 animate-fade-in">
      {/* Header — stacks vertically on phone, side-by-side from sm+ */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <CheckSquare className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            Task Manager
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Capture, triage, and complete</p>
        </div>
        
        <div className="flex gap-2">
          {/* Create Task Button */}
          <Button 
            onClick={() => setShowCreateDialog(true)}
            size="sm"
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 h-9"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>
      
      {/* Filter — horizontal scroll on mobile so pills stay on a single line */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 flex-nowrap sm:flex-wrap sm:overflow-visible">
        {["all", "Today", "ThisWeek", "ParkingLot"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase()}`}
            className="shrink-0"
          >
            {f === "all" ? "All" : priorityConfig[f]?.label || f}
          </Button>
        ))}
        <Button
          variant={filter === "recurring" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("recurring")}
          className={`shrink-0 ${filter === "recurring" ? "bg-violet-600" : ""}`}
        >
          <Repeat className="w-3 h-3 mr-1" />
          Recurring
        </Button>
        <Button
          variant={showCompleted ? "default" : "outline"}
          size="sm"
          onClick={() => setShowCompleted(v => !v)}
          className={`shrink-0 sm:ml-auto ${showCompleted ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
          data-testid="toggle-completed-btn"
          title="Hidden by default so your list stays clean"
        >
          <span className="hidden sm:inline">{showCompleted ? "Hide" : "Show"} completed</span>
          <span className="sm:hidden">{showCompleted ? "Hide" : "Done"}</span>
          {completedCount > 0 && (
            <span className="ml-1.5 text-[10px] opacity-70">({completedCount})</span>
          )}
        </Button>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(filter === "recurring"
          ? { "Recurring": visibleTasks.filter(t => t.recurrence) }
          : filteredGroups
        ).map(([priority, priorityTasks]) => (
          <Card key={priority} className="bento-card kanban-column">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                {filter === "recurring" ? (
                  <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                    <Repeat className="w-3 h-3 mr-1" />
                    Recurring Tasks
                  </Badge>
                ) : (
                  <Badge className={priorityConfig[priority]?.className}>
                    {priorityConfig[priority]?.label}
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  {priorityTasks.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {priorityTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No tasks
                </p>
              ) : (
                priorityTasks.map((task) => {
                  const CategoryIcon = categoryIcons[task.category] || Target;
                  return (
                    <div
                      key={task.task_id}
                      data-testid={`task-${task.task_id}`}
                      className={`p-3 rounded-lg border border-zinc-800 bg-zinc-900/50 space-y-2
                        ${task.completed ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={task.completed}
                          onCheckedChange={() => toggleComplete(task)}
                          className="mt-1"
                          data-testid={`task-checkbox-${task.task_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${task.completed ? 'line-through' : ''}`}>
                            {task.description}
                          </p>
                          {task.next_step && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" />
                              {task.next_step}
                            </p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {Object.keys(priorityConfig)
                              .filter(p => p !== priority)
                              .map(p => (
                                <DropdownMenuItem
                                  key={p}
                                  onClick={() => updatePriority(task, p)}
                                >
                                  Move to {priorityConfig[p].label}
                                </DropdownMenuItem>
                              ))}
                            <DropdownMenuItem
                              className="text-red-400"
                              onClick={() => deleteTask(task.task_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <CategoryIcon className={`w-3 h-3 ${categoryColors[task.category]}`} />
                        <span className="text-xs text-muted-foreground">{task.category}</span>
                        {task.source === "flight_plan" && (
                          <Badge variant="outline" className="text-xs bg-violet-500/10 border-violet-500/30 text-violet-400">
                            <Plane className="w-3 h-3 mr-1" />
                            Flight Plan
                          </Badge>
                        )}
                        {task.source === "sprint" && (
                          <Badge variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                            Sprint
                          </Badge>
                        )}
                        {task.recurrence && recurrenceConfig[task.recurrence] && (
                          <Badge variant="outline" className={`text-xs ${recurrenceConfig[task.recurrence].color}`}>
                            <Repeat className="w-3 h-3 mr-1" />
                            {recurrenceConfig[task.recurrence].label}
                          </Badge>
                        )}
                        {task.due_date && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-400" />
              Create New Task
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Description</Label>
              <Input
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="What needs to be done?"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Safety">Safety</SelectItem>
                    <SelectItem value="Staff">Staff</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Strategic">Strategic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Today">Today</SelectItem>
                    <SelectItem value="ThisWeek">This Week</SelectItem>
                    <SelectItem value="ParkingLot">Parking Lot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-violet-400" />
                Recurrence (Optional)
              </Label>
              <Select value={newTask.recurrence || "none"} onValueChange={(v) => setNewTask({ ...newTask, recurrence: v === "none" ? null : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                  <SelectValue placeholder="One-time task" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">One-time task</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Recurring tasks auto-renew when completed
              </p>
            </div>

            {newTask.recurrence && (
              <div>
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  First Due Date
                </Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={createTask} className="bg-gradient-to-r from-violet-600 to-fuchsia-600">
              {newTask.recurrence ? (
                <>
                  <Repeat className="w-4 h-4 mr-2" />
                  Create Recurring Task
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
